import { useState, useEffect, useRef } from 'react';
import { Button, Dialog } from 'antd-mobile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    Check,
    Crown,
    Sparkles,
    Loader
} from 'lucide-react';
import { useStore } from '../store';
import { api } from '../api';
import './UpgradePage.css';

import type { SubscriptionPlan } from '@tangtung/shared';

export default function UpgradePage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const dbUser = useStore((s) => s.dbUser);
    const setDbUser = useStore((s) => s.setDbUser);
    const showToast = useStore((s) => s.showToast);

    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [currentTier, setCurrentTier] = useState<string>('FREE');
    const [expiresAt, setExpiresAt] = useState<string | null>(null);
    const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [paymentChecking, setPaymentChecking] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Clean up polling on unmount
    useEffect(() => {
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, []);

    // Fetch plans from API
    useEffect(() => {
        api.getSubscriptionPlans().then(setPlans).catch(console.error);
    }, []);

    useEffect(() => {
        fetchSubscription();
    }, [dbUser]);

    // Handle payment callback from Beam redirect
    useEffect(() => {
        const paymentStatus = searchParams.get('payment');
        const paymentId = searchParams.get('payment_id');

        if (paymentStatus === 'success' && paymentId && dbUser) {
            handlePaymentSuccess(parseInt(paymentId, 10));
            // Clean up URL params
            setSearchParams({}, { replace: true });
        } else if (paymentStatus === 'cancelled') {
            showToast('ยกเลิกการชำระเงิน');
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, dbUser]);

    const fetchSubscription = async () => {
        if (!dbUser) return;

        try {
            const res = await api.getSubscription(dbUser.line_user_id);
            setCurrentTier(res.tier);
            setExpiresAt(res.expires_at);
            setDaysRemaining(res.days_remaining);
        } catch (error) {
            console.error('Error fetching subscription:', error);
        }
    };

    const handlePaymentSuccess = async (paymentId: number) => {
        if (!dbUser) return;

        setPaymentChecking(true);
        let attempts = 0;
        const maxAttempts = 10;

        // Poll payment status until completed or timeout
        pollRef.current = setInterval(async () => {
            attempts++;
            try {
                const payment = await api.getPaymentStatus(dbUser.line_user_id, paymentId);

                if (payment.status === 'completed') {
                    if (pollRef.current) clearInterval(pollRef.current);
                    pollRef.current = null;
                    setPaymentChecking(false);

                    showToast('ชำระเงินสำเร็จ! อัปเกรดเรียบร้อย');

                    // Refresh user data
                    const { data: updatedUser } = await api.getUser(dbUser.line_user_id);
                    if (updatedUser) setDbUser(updatedUser);
                    await fetchSubscription();
                } else if (payment.status === 'failed') {
                    if (pollRef.current) clearInterval(pollRef.current);
                    pollRef.current = null;
                    setPaymentChecking(false);
                    showToast('การชำระเงินล้มเหลว กรุณาลองใหม่');
                }
            } catch (error) {
                console.error('Error checking payment:', error);
            }

            if (attempts >= maxAttempts) {
                if (pollRef.current) clearInterval(pollRef.current);
                pollRef.current = null;
                setPaymentChecking(false);

                // Even if polling times out, refresh subscription in case webhook already processed
                await fetchSubscription();
                const { data: updatedUser } = await api.getUser(dbUser.line_user_id);
                if (updatedUser) setDbUser(updatedUser);
            }
        }, 3000); // Poll every 3 seconds
    };

    const handleUpgrade = async (tier: 'BASIC' | 'PRO' | 'TEST_PRO') => {
        if (!dbUser) return;

        const plan = plans.find(p => p.tier === tier);
        const isRenewal = tier === currentTier && daysRemaining !== null && daysRemaining > 0;

        let dialogContent: string;
        let dialogTitle: string;

        if (isRenewal) {
            const totalDays = daysRemaining + 30;
            dialogTitle = `ต่ออายุ ${plan?.name}`;
            dialogContent = `ต่ออายุแพ็กเกจ ${plan?.name} อีก 30 วัน\nเหลือ ${daysRemaining} + 30 = ${totalDays} วัน\nราคา ฿${plan?.price}`;
        } else {
            dialogTitle = `อัปเกรดเป็น ${plan?.name}`;
            dialogContent = `สมัครแพ็กเกจ ${plan?.name} ราคา ฿${plan?.price}/เดือน\nระบบจะพาไปหน้าชำระเงิน`;
        }

        const result = await Dialog.confirm({
            title: dialogTitle,
            content: dialogContent,
            confirmText: 'ไปชำระเงิน',
            cancelText: 'ยกเลิก',
        });

        if (!result) return;

        setLoading(true);
        setSelectedPlan(tier);

        try {
            const paymentResult = await api.createPayment(dbUser.line_user_id, tier);

            if (paymentResult.success && paymentResult.checkout_url) {
                // Redirect to Beam checkout page
                window.location.href = paymentResult.checkout_url;
            } else if (paymentResult.fallback) {
                showToast(paymentResult.message || 'ไม่สามารถเชื่อมต่อระบบชำระเงินได้ กรุณาลองใหม่');
            }
        } catch (error: any) {
            showToast(error.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
        } finally {
            setLoading(false);
            setSelectedPlan(null);
        }
    };

    const [selectedPlanTier, setSelectedPlanTier] = useState<string | null>(null);

    // Set default selection to recommended or current
    useEffect(() => {
        if (plans.length > 0 && !selectedPlanTier) {
            // Default to PRO if current is FREE, otherwise current
            if (currentTier === 'FREE') {
                setSelectedPlanTier('PRO');
            } else {
                setSelectedPlanTier(currentTier);
            }
        }
    }, [plans, currentTier]);

    // Update selection when plans load
    useEffect(() => {
        if (!selectedPlanTier && plans.length > 0) {
            const proPlan = plans.find(p => p.tier === 'PRO');
            if (proPlan) setSelectedPlanTier('PRO');
        }
    }, [plans]);

    const handleMainAction = () => {
        if (!selectedPlanTier) return;
        handleUpgrade(selectedPlanTier as 'BASIC' | 'PRO' | 'TEST_PRO');
    };

    const getPlanDetail = (tier: string) => plans.find(p => p.tier === tier);
    const selectedPlanData = selectedPlanTier ? getPlanDetail(selectedPlanTier) : null;
    const isCurrentPlan = selectedPlanTier === currentTier;

    return (
        <div className="page upgrade-page-premium">
            <div className="upgrade-header-simple">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={24} />
                </button>
                <h1>อัปเกรดแพ็กเกจ</h1>
            </div>

            <div className="upgrade-content">
                {/* Current Status Banner */}
                {currentTier !== 'FREE' && daysRemaining !== null && (
                    <div className="status-banner">
                        <div className="status-icon">
                            <Crown size={20} color="#fff" fill="#fff" />
                        </div>
                        <div className="status-text">
                            <span>แพ็กเกจปัจจุบัน: {currentTier}</span>
                            <small>เหลืออีก {daysRemaining} วัน</small>
                        </div>
                    </div>
                )}

                <div className="plans-grid">
                    {plans.filter(p => p.tier !== 'TEST_PRO').map((plan) => {
                        const isSelected = selectedPlanTier === plan.tier;
                        const isPro = plan.tier === 'PRO';
                        const isCurrent = plan.tier === currentTier;

                        return (
                            <div
                                key={plan.tier}
                                className={`premium-plan-card ${isSelected ? 'selected' : ''} ${isPro ? 'pro-tier' : ''}`}
                                onClick={() => setSelectedPlanTier(plan.tier)}
                            >
                                {isPro && <div className="best-value-badge"><Sparkles size={12} fill="white" /> แนะนำ</div>}
                                {isCurrent && <div className="current-badge">ใช้งานอยู่</div>}

                                <div className="plan-header-row">
                                    <div className="plan-name">{plan.name}</div>
                                    <div className="plan-check-icon">
                                        {isSelected && <div className="check-circle"><Check size={14} /></div>}
                                    </div>
                                </div>

                                <div className="plan-price-row">
                                    {plan.price === 0 ? (
                                        <div className="price-text free">ฟรี</div>
                                    ) : (
                                        <>
                                            <div className="price-text">
                                                <span className="currency">฿</span>
                                                {plan.price}
                                                <span className="period">/เดือน</span>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                                ตกวันละ {Math.ceil(plan.price / 30)} บาท
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="plan-features-mini">
                                    {plan.features.slice(0, 2).map((f, i) => (
                                        <span key={i} className="mini-feature">✓ {f}</span>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Selected Plan Details Area (Sticky Bottom or just below) */}
                <div className="faq-section-minimal">
                    <h3>คำถามที่พบบ่อย</h3>
                    <div className="faq-grid">
                        <div className="faq-item-mini">
                            <strong>ทดลองฟรี 14 วัน?</strong>
                            <p>ใช้ฟรีทุกฟีเจอร์ ไม่ต้องผูกบัตร</p>
                        </div>
                        <div className="faq-item-mini">
                            <strong>Staff คืออะไร?</strong>
                            <p>พนักงานคิดเงินได้ แต่แก้ไม่ได้</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fixed Bottom Action Bar */}
            <div className="bottom-action-bar">
                <div className="selected-summary">
                    <span>{selectedPlanData?.name || 'เลือกแพ็กเกจ'}</span>
                    <strong>{selectedPlanData?.price === 0 ? 'ฟรี' : `฿${selectedPlanData?.price || 0}/เดือน`}</strong>
                </div>
                <Button
                    block
                    color="primary"
                    size="large"
                    disabled={!selectedPlanTier || (isCurrentPlan && !(daysRemaining && daysRemaining < 5))}
                    loading={loading}
                    onClick={handleMainAction}
                    className="upgrade-main-btn"
                >
                    {isCurrentPlan ? 'แพ็กเกจปัจจุบัน' : selectedPlanData?.price === 0 ? 'ใช้งานฟรี' : 'สมัครเลย'}
                </Button>
            </div>

            {/* Payment Loading Overlay */}
            {paymentChecking && (
                <div className="payment-checking-overlay">
                    <div className="payment-checking-box">
                        <Loader size={32} className="spin" color="#4facfe" />
                        <h3>กำลังตรวจสอบ...</h3>
                        <p>กรุณารอสักครู่ ระบบกำลังยืนยันการชำระเงิน</p>
                    </div>
                </div>
            )}
        </div>
    );
}
